import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Upload, Spin, Empty, Modal } from 'antd';
import { ArrowLeftOutlined, UploadOutlined, PlayCircleOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';

import { useResponsive } from '../../hooks/useResponsive';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';
import { IAssessmentsState } from '../../store/reducers/assessments/types';
import { IClassesState } from '../../store/reducers/classes/types';
import { URLs } from '../../store/urls';

// Styled Components
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  .right-section {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .mode-label {
      font-size: 15px;
      font-weight: 700;
      color: #1a1a1a;
      white-space: nowrap;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    .right-section {
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const BackButton = styled(BusinessButton)`
  flex-shrink: 0;
`;

const TitleSection = styled.div`
  flex: 1;
  
  h1 {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    
    .icon {
      font-size: 32px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: #666;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 22px;
      
      .icon {
        font-size: 26px;
      }
    }
    
    .subtitle {
      font-size: 13px;
    }
  }
`;

const MainContent = styled.div<{ isAutoMode?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.isAutoMode ? '1fr 380px' : '300px 1fr 380px'};
  gap: 24px;
  
  @media (max-width: 1400px) {
    grid-template-columns: ${props => props.isAutoMode ? '1fr 380px' : '280px 1fr 340px'};
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StudentsPanel = styled(ContentCard)`
  height: fit-content;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  
  @media (max-width: 1024px) {
    order: 1;
    max-height: 400px;
  }
`;

const StudentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StudentItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  
  ${props => props.selected ? `
    background: rgba(22, 119, 255, 0.08);
    border-color: rgba(22, 119, 255, 0.3);
  ` : `
    background: rgba(0, 0, 0, 0.02);
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      border-color: rgba(0, 0, 0, 0.1);
    }
  `}
  
  .status-icon {
    font-size: 20px;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.approved {
      color: #52c41a;
    }
    
    &.pending {
      color: #faad14;
    }
  }
  
  .student-info {
    flex: 1;
    min-width: 0;
    
    .name {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .score {
      font-size: 12px;
      color: #666;
      font-weight: 500;
    }
  }
  
  .status-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    
    &.approved {
      color: #52c41a;
    }
    
    &.pending {
      color: #faad14;
    }
  }
`;

const GradingPanel = styled(ContentCard)``;

const ToggleContainer = styled.div`
  position: relative;
  display: inline-flex;
  background: linear-gradient(135deg, #4E9FFF 0%, #2563EB 50%, #52C41A 50%, #389E0D 100%);
  border-radius: 50px;
  padding: 3px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const ToggleButton = styled.button<{ mode: 'manual' | 'automatic' }>`
  position: relative;
  padding: 3px 8px;
  border: none;
  background: ${props => props.mode === 'manual' 
    ? 'linear-gradient(135deg, #4E9FFF 0%, #2563EB 100%)'
    : 'linear-gradient(135deg, #52C41A 0%, #389E0D 100%)'
  };
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  width: 140px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 1;
  
  &:hover {
    transform: scale(1.03);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  .text {
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 11px;
    white-space: nowrap;
    font-weight: 600;
    flex: 1;
    text-align: center;
  }
  
  .icon-circle {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    width: 120px;
    padding: 3px 6px;
    
    .text {
      font-size: 10px;
    }
    
    .icon-circle {
      width: 26px;
      height: 26px;
      font-size: 14px;
    }
  }
`;

const ModeContent = styled.div`
  padding: 24px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
`;

const AutomaticModeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UploadSection = styled.div`
  .upload-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
  
  .upload-hint {
    font-size: 12px;
    color: #666;
    margin-top: 8px;
  }
`;

const StyledUpload = styled(Upload)`
  .ant-upload {
    width: 100%;
  }
`;

const ActionButton = styled(BusinessButton)`
  width: 100%;
  margin-top: 12px;
`;

const ManualModeSection = styled.div`
  text-align: center;
  padding: 40px 20px;
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #666;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 13px;
  }
`;

// Answer Sheet Viewer Components
const AnswerSheetViewer = styled(ContentCard)`
  height: fit-content;
  
  @media (max-width: 1400px) {
    grid-column: span 2;
  }
  
  @media (max-width: 1024px) {
    grid-column: span 1;
    order: 2;
  }
`;

const AutoModeContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  height: fit-content;
  overflow: hidden;
  
  @media (max-width: 1400px) {
    grid-column: span 2;
  }
  
  @media (max-width: 1024px) {
    grid-column: span 1;
    order: 2;
  }
`;

const AnswerSheetContainer = styled.div<{ alignTop?: boolean }>`
  width: 100%;
  background: #f5f5f5;
  border-radius: 12px;
  padding: ${props => props.alignTop ? '0 20px 20px 20px' : '20px 20px 0 20px'};
  display: flex;
  justify-content: center;
  align-items: ${props => props.alignTop ? 'flex-start' : 'center'};
  min-height: ${props => props.alignTop ? 'auto' : '600px'};
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

// Manual Grading Panel Components
const ManualGradingPanel = styled(ContentCard)`
  height: fit-content;
  
  @media (max-width: 1400px) {
    /* Don't hide on smaller screens, just reposition in grid */
    grid-column: span 1;
  }
  
  @media (max-width: 1024px) {
    order: 3;
  }
`;

const GradingScaleSection = styled.div`
  margin-bottom: 24px;
  
  .scale-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
`;

const GradeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  margin-bottom: 8px;
  
  .grade-letter {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
  }
  
  .grade-range {
    font-size: 14px;
    color: #666;
    font-weight: 500;
  }
`;

const ScoreInputSection = styled.div`
  margin-bottom: 24px;
  
  .input-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
`;

const ScoreInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.1);
  }
  
  &:disabled {
    background: rgba(0, 0, 0, 0.04);
    cursor: not-allowed;
  }
`;

const GradeResultSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(22, 119, 255, 0.08) 0%, rgba(82, 196, 26, 0.08) 100%);
  border-radius: 12px;
  text-align: center;
  
  .grade-label {
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
  }
  
  .grade-value {
    font-size: 48px;
    font-weight: 700;
    color: #1677ff;
    line-height: 1;
  }
`;

const ApproveButton = styled(BusinessButton)`
  width: 100%;
`;

const GradingAssessmentPage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get assessment ID from URL
  const assessmentId = searchParams.get('assessmentId');
  
  // Redux
  const { assessments } = useTypedSelector<IAssessmentsState>(state => state.assessments);
  const { classes } = useTypedSelector<IClassesState>(state => state.classes);
  const { getAssessmentsRequest, getClassesRequest } = useActions();
  
  // Local state
  const [gradingMode, setGradingMode] = useState<'manual' | 'automatic'>('manual');
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [answerKeyFile, setAnswerKeyFile] = useState<any>(null);
  const [correctAnswers, setCorrectAnswers] = useState<string>(''); // Number of correct answers
  const [finalScore, setFinalScore] = useState<string>(''); // Calculated score (kept for compatibility)
  const [students, setStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [answerSheet, setAnswerSheet] = useState<any>(null);
  const [loadingAnswerSheet, setLoadingAnswerSheet] = useState(false);
  const [gradingScaleData, setGradingScaleData] = useState<any>(null); // Full grading scale object with type
  const [gradingScale, setGradingScale] = useState<any[]>([ // Ranges only (for backward compatibility)
    { grade: 'A', min: 45, max: 50 },
    { grade: 'B', min: 38, max: 44 },
    { grade: 'C', min: 30, max: 37 },
    { grade: 'D', min: 0, max: 29 },
  ]);
  
  // Auto-grading states
  const [autoGradingStatus, setAutoGradingStatus] = useState<'idle' | 'processing' | 'completed' | 'error'>('idle');
  const [gradingJobId, setGradingJobId] = useState<string | null>(null);
  const [autoGradingResults, setAutoGradingResults] = useState<any>(null);
  const [showApproveAllModal, setShowApproveAllModal] = useState(false);
  
  // Find current assessment from Redux store
  const assessment: any = assessments?.find((a: any) => a.id === Number(assessmentId));
  const assessmentName = assessment?.name || assessment?.examName || 'Assessment';
  const totalStudents = students.length;
  const totalQuestions = assessment?.questions;

  useEffect(() => {
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      getAssessmentsRequest(); // Load assessments into Redux store
      getClassesRequest();
    }
  }, []);

  // Fetch students data when assessment is loaded
  useEffect(() => {
    const fetchStudents = async () => {
      if (!assessmentId) return;

      setLoadingStudents(true);
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch(
          `${URLs.BASE_URL}api/assessments/${assessmentId}/students`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch students');
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          const studentsList = result.data.students || [];
          setStudents(studentsList);
          console.log('[GradingAssessment] Students loaded:', studentsList.length);
          console.log('[GradingAssessment] Students data:', studentsList);
          
          // Log answer sheet IDs
          studentsList.forEach((s: any) => {
            console.log(`  - Student ${s.name}: answerSheetId=${s.answerSheetId}, status=${s.status}`);
          });
        }
      } catch (error: any) {
        console.error('Error fetching students:', error);
        notificationController.error({
          message: 'Failed to load students',
          description: error.message || 'Please try again.'
        });
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudents();
  }, [assessmentId]);

  // Fetch grading scale when assessment is loaded
  useEffect(() => {
    const fetchGradingScale = async () => {
      if (!assessment?.gradingScaleId) {
        console.log('[GradingAssessment] No gradingScaleId found in assessment:', assessment);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        console.log('[GradingAssessment] Fetching grading scale:', assessment.gradingScaleId);
        const response = await fetch(
          `${URLs.BASE_URL}api/grading-scales/${assessment.gradingScaleId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch grading scale: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          console.log('[GradingAssessment] Raw grading scale data:', result.data);
          
          setGradingScaleData(result.data); // Save full data including type
          
          // Transform ranges from API format (minValue/maxValue) to component format (min/max)
          const ranges = (result.data.ranges || []).map((range: any) => ({
            id: range.id,
            grade: range.grade,
            min: range.minValue || range.min,
            max: range.maxValue || range.max,
            description: range.description
          }));
          
          setGradingScale(ranges);
          
          console.log('[GradingAssessment] ✅ Grading scale loaded successfully!');
          console.log('[GradingAssessment] Grading scale name:', result.data.name);
          console.log('[GradingAssessment] Grading scale type:', result.data.type);
          console.log('[GradingAssessment] Transformed ranges:', ranges);
        } else {
          console.error('[GradingAssessment] Invalid grading scale response:', result);
        }
      } catch (error: any) {
        console.error('[GradingAssessment] ❌ Error fetching grading scale:', error);
        notificationController.error({
          message: 'Failed to load grading scale',
          description: error.message || 'Using default grading scale.'
        });
        // Keep default grading scale on error
      }
    };

    fetchGradingScale();
  }, [assessment]);

  // Polling for auto-grading results
  useEffect(() => {
    let pollingInterval: NodeJS.Timeout | null = null;

    const pollGradingResults = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${URLs.BASE_URL}api/assessments/${assessmentId}/grading-results`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          console.error('[GradingAssessment] Failed to fetch grading results:', response.status);
          return;
        }

        const result = await response.json();
        console.log('[GradingAssessment] Grading results:', result);

        if (result.success && result.data) {
          setAutoGradingResults(result.data);
          setAutoGradingStatus('completed');
          
          // Update students list
          if (result.data.students) {
            setStudents(result.data.students.map((s: any) => ({
              id: s.studentId,
              name: s.studentName,
              score: s.score,
              totalScore: s.totalScore,
              grade: s.grade,
              status: s.isApproved ? 'approved' : 'pending',
              comments: s.comments,
              gradedAt: s.gradedAt
            })));
          }
          
          // Stop polling
          if (pollingInterval) {
            clearInterval(pollingInterval);
          }
        }
      } catch (error: any) {
        console.error('[GradingAssessment] Error polling grading results:', error);
      }
    };

    if (autoGradingStatus === 'processing') {
      // Start polling every 2 seconds
      pollingInterval = setInterval(pollGradingResults, 2000);
      
      // Also poll immediately
      pollGradingResults();
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [autoGradingStatus, assessmentId]);

  const handleBack = () => {
    navigate('/grading');
  };


  const handleStudentClick = async (studentId: number) => {
    console.log('=================================');
    console.log('[GradingAssessment] 🔴 STUDENT CLICKED:', studentId);
    console.log('[GradingAssessment] Current mode:', gradingMode);
    console.log('[GradingAssessment] Students list:', students);
    console.log('=================================');
    
    setSelectedStudent(studentId);
    setAnswerSheet(null);
    
    // Load student's score if already graded
    const student = students.find(s => s.id === studentId);
    console.log('[GradingAssessment] Student data:', student);
    
    if (student && student.score !== null && student.score !== undefined) {
      // If student already has a score, try to back-calculate correct answers
      // Or just set the score directly
      setFinalScore(student.score.toString());
      setCorrectAnswers(''); // Reset correct answers input
      console.log('[GradingAssessment] Student already has score:', student.score);
    } else {
      setFinalScore('');
      setCorrectAnswers('');
    }

    // Fetch answer sheet if exists
    console.log('[GradingAssessment] 🔍 Checking answerSheetId:', student?.answerSheetId);
    console.log('[GradingAssessment] 🔍 Student exists:', !!student);
    console.log('[GradingAssessment] 🔍 Condition check:', student && student.answerSheetId);
    
    if (student && student.answerSheetId) {
      console.log('[GradingAssessment] ✅ Fetching answer sheet ID:', student.answerSheetId);
      setLoadingAnswerSheet(true);
      try {
        const token = localStorage.getItem('token');
        const url = `${URLs.BASE_URL}${URLs.GET_UPLOADED_ANSWER_SHEET_ENDPOINT}/${student.answerSheetId}/uploaded`;
        console.log('[GradingAssessment] 📡 Request URL:', url);
        console.log('[GradingAssessment] 🔑 Token exists:', !!token);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('[GradingAssessment] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[GradingAssessment] Error response:', errorText);
          throw new Error(`Failed to fetch answer sheet: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('[GradingAssessment] 📦 Answer sheet result:', result);
        console.log('[GradingAssessment] 📦 Full response data:', JSON.stringify(result, null, 2));
        
        if (result.success && result.data) {
          console.log('[GradingAssessment] 📄 Answer sheet data:', result.data);
          console.log('[GradingAssessment] 📄 All fields in data:', Object.keys(result.data));
          console.log('[GradingAssessment] 🖼️ imageUrl:', result.data.imageUrl);
          console.log('[GradingAssessment] 🖼️ url:', result.data.url);
          console.log('[GradingAssessment] 🖼️ filePath:', result.data.filePath);
          console.log('[GradingAssessment] 🖼️ fileName:', result.data.fileName);
          console.log('[GradingAssessment] 🖼️ imageBase64:', result.data.imageBase64 ? 'exists' : 'null');
          
          // Construct proper image URL for viewing the file
          const modifiedData = {
            ...result.data,
            // Use the generated answer sheet ID to construct a proper download/view URL
            imageUrl: `${URLs.BASE_URL}${URLs.GET_UPLOADED_ANSWER_SHEET_ENDPOINT}/${student.answerSheetId}/uploaded/file`
          };
          
          console.log('[GradingAssessment] 🔗 Constructed image URL:', modifiedData.imageUrl);
          setAnswerSheet(modifiedData);
          console.log('[GradingAssessment] ✅ Answer sheet loaded successfully');
        } else {
          console.warn('[GradingAssessment] ⚠️ Answer sheet data not found in response');
          console.warn('[GradingAssessment] Response structure:', Object.keys(result));
        }
      } catch (error: any) {
        console.error('[GradingAssessment] Error fetching answer sheet:', error);
        notificationController.error({
          message: 'Failed to load answer sheet',
          description: error.message || 'Please try again.'
        });
      } finally {
        setLoadingAnswerSheet(false);
      }
    } else {
      console.warn('[GradingAssessment] Student has no answerSheetId:', student?.name);
    }
  };
  
  // Calculate final score based on correct answers
  // Final score is simply the number of correct answers (not a percentage)
  const calculateFinalScore = (correct: number): number => {
    return correct;
  };

  // Calculate grade based on score and grading scale type
  // minValue/maxValue from API are always in range 0-100 (percentage scale)
  const calculateGrade = (score: number): string => {
    if (!gradingScaleData || !gradingScale || gradingScale.length === 0) {
      return '—';
    }

    if (totalQuestions === 0) {
      return '—';
    }

    const scaleType = gradingScaleData.type?.toLowerCase();
    console.log('[GradingAssessment] Calculating grade for score:', score, 'Total questions:', totalQuestions, 'Type:', scaleType);

    // ALWAYS convert score to percentage (0-100) because minValue/maxValue are always in percentage scale
    const scorePercentage = Math.round((score / totalQuestions) * 100);
    console.log('[GradingAssessment] Score as percentage:', scorePercentage, '%');
    
    // Find matching range (min/max are in percentage scale 0-100)
    for (const scale of gradingScale) {
      const min = parseFloat(scale.min);
      const max = parseFloat(scale.max);
      
      if (scorePercentage >= min && scorePercentage <= max) {
        console.log('[GradingAssessment] ✅ Matched range:', scale.grade, `(${min}%-${max}%)`);
        return scale.grade;
      }
    }
    
    console.log('[GradingAssessment] ❌ No matching range found for', scorePercentage, '%');
    return '—';
  };
  
  const handleCorrectAnswersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value);
      // Check if within range (0 to totalQuestions)
      if (value === '' || (numValue >= 0 && numValue <= totalQuestions)) {
        setCorrectAnswers(value);
        // Auto-calculate final score (same as correct answers)
        if (value !== '') {
          const calculatedScore = calculateFinalScore(numValue);
          setFinalScore(calculatedScore.toString());
        } else {
          setFinalScore('');
        }
      }
    }
  };
  
  const handleApproveStudent = async () => {
    if (!selectedStudent) {
      notificationController.error({
        message: 'No Student Selected',
        description: 'Please select a student first.'
      });
      return;
    }
    
    if (!correctAnswers || correctAnswers === '') {
      notificationController.error({
        message: 'No Correct Answers Entered',
        description: 'Please enter the number of correct answers.'
      });
      return;
    }
    
    if (!finalScore || finalScore === '') {
      notificationController.error({
        message: 'Score Not Calculated',
        description: 'Score should be calculated automatically.'
      });
      return;
    }
    
    const correctAnswersNum = parseInt(correctAnswers);
    const score = parseInt(finalScore);
    const grade = calculateGrade(score);
    const student = students.find(s => s.id === selectedStudent);
    
    const requestBody = {
      finalScore: correctAnswersNum,  // Number of correct answers
      grade: grade                    // Calculated grade (letter/number)
    };
    
    console.log('[GradingAssessment] Approving student:', {
      studentId: selectedStudent,
      assessmentId: assessmentId,
      requestBody: requestBody
    });
    console.log('[GradingAssessment] Request body JSON:', JSON.stringify(requestBody, null, 2));
    
    try {
      const token = localStorage.getItem('token');
      
      // Approve student with score, grade, and comments
      const approveResponse = await fetch(
        `${URLs.BASE_URL}api/assessments/${assessmentId}/students/${selectedStudent}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      console.log('[GradingAssessment] Response status:', approveResponse.status);
      
      if (!approveResponse.ok) {
        const errorText = await approveResponse.text();
        console.error('[GradingAssessment] ❌ Approve failed!');
        console.error('[GradingAssessment] Status:', approveResponse.status, approveResponse.statusText);
        console.error('[GradingAssessment] Error response:', errorText);
        throw new Error(`Failed to approve student: ${errorText || approveResponse.statusText}`);
      }

      const result = await approveResponse.json();
      console.log('[GradingAssessment] ✅ Student approved successfully:', result);

      notificationController.success({
        message: 'Student Approved',
        description: `${student?.name} has been approved with ${correctAnswersNum}/${totalQuestions} correct answers (Grade: ${grade})`
      });
      
      // Update student status locally
      setStudents(prevStudents => 
        prevStudents.map(s => 
          s.id === selectedStudent 
            ? { ...s, score: correctAnswersNum, grade: grade, status: 'approved' }
            : s
        )
      );
      
      // Clear selection and score
      setSelectedStudent(null);
      setCorrectAnswers('');
      setFinalScore('');
      
    } catch (error: any) {
      console.error('Error approving student:', error);
      notificationController.error({
        message: 'Failed to approve student',
        description: error.message || 'Please try again.'
      });
    }
  };

  // Check if all students are approved
  const allStudentsApproved = students.length > 0 && students.every((s: any) => s.status === 'approved');
  
  const handleEndAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const assessmentType = assessment?.type || 'assessment';
      
      console.log('[GradingAssessment] Ending assessment:', assessmentId);
      
      const response = await fetch(
        `${URLs.BASE_URL}${URLs.UPDATE_ASSESSMENT_ENDPOINT}/${assessmentId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: 'done'
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GradingAssessment] End assessment failed:', response.status, errorText);
        throw new Error(`Failed to end assessment: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('[GradingAssessment] ✅ Assessment ended successfully:', result);

      notificationController.success({
        message: `${assessmentType.charAt(0).toUpperCase() + assessmentType.slice(1)} Ended`,
        description: 'All students have been graded and approved.',
        duration: 3
      });
      
      // Navigate to grading page
      setTimeout(() => {
        navigate('/grading');
      }, 1000);
      
    } catch (error: any) {
      console.error('[GradingAssessment] Error ending assessment:', error);
      notificationController.error({
        message: 'Failed to End Assessment',
        description: error.message || 'Please try again.'
      });
    }
  };

  const handleApproveAll = async () => {
    setShowApproveAllModal(false);
    
    try {
      const token = localStorage.getItem('token');
      
      console.log('[GradingAssessment] Approving all students for assessment:', assessmentId);
      
      const response = await fetch(
        `${URLs.BASE_URL}api/assessments/${assessmentId}/students/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GradingAssessment] Approve all failed:', response.status, errorText);
        throw new Error(`Failed to approve all students: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('[GradingAssessment] ✅ Approve all response:', result);

      if (result.success && result.data) {
        notificationController.success({
          message: 'All Students Approved',
          description: `Successfully approved ${result.data.totalApproved} students.`,
          duration: 3
        });
        
        // Update students status locally
        setStudents(prevStudents => 
          prevStudents.map(s => ({ ...s, status: 'approved' }))
        );
      }
      
    } catch (error: any) {
      console.error('[GradingAssessment] Error approving all students:', error);
      notificationController.error({
        message: 'Failed to Approve All',
        description: error.message || 'Please try again.'
      });
    }
  };

  const handleRunAutomaticGrading = async () => {
    if (!answerKeyFile) {
      notificationController.error({
        message: 'No Answer Key',
        description: 'Please upload an answer key CSV file first.'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Create FormData with the CSV file
      const formData = new FormData();
      formData.append('file', answerKeyFile);
      
      console.log('[GradingAssessment] Running automatic grading:', {
        assessmentId: assessmentId,
        fileName: answerKeyFile.name,
        fileSize: answerKeyFile.size
      });

      // Send POST request with CSV file for automatic grading
      const response = await fetch(
        `${URLs.BASE_URL}api/assessments/${assessmentId}/auto-grade`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        }
      );

      console.log('[GradingAssessment] Auto-grade response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GradingAssessment] Auto-grade failed:', response.status, errorText);
        throw new Error(`Failed to run automatic grading: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('[GradingAssessment] ✅ Automatic grading response:', result);

      if (result.success && result.data) {
        const { status, gradingJobId, totalStudents } = result.data;
        
        setGradingJobId(gradingJobId || null);
        
        if (status === 'processing') {
          // Start polling for results
          setAutoGradingStatus('processing');
          
          notificationController.info({
            message: 'Grading In Progress',
            description: `Auto-grading ${totalStudents} students. Please wait...`,
            duration: 3
          });
        } else if (status === 'completed') {
          // Immediately completed
          setAutoGradingStatus('completed');
          
          notificationController.success({
            message: 'Automatic Grading Complete',
            description: `Successfully graded ${totalStudents} students.`
          });
        }
        
        // Clear answer key file
        setAnswerKeyFile(null);
      }
      
    } catch (error: any) {
      console.error('[GradingAssessment] Error running automatic grading:', error);
      setAutoGradingStatus('error');
      notificationController.error({
        message: 'Automatic Grading Failed',
        description: error.message || 'Please try again.'
      });
    }
  };

  const uploadProps: UploadProps = {
    accept: '.csv',
    maxCount: 1,
    beforeUpload: (file) => {
      setAnswerKeyFile(file);
      notificationController.success({
        message: 'Answer Key Uploaded',
        description: `File "${file.name}" uploaded successfully.`
      });
      return false;
    },
    onRemove: () => {
      setAnswerKeyFile(null);
    },
  };

  const handleRunAutoGrading = () => {
    if (!answerKeyFile) {
      notificationController.error({
        message: 'No Answer Key',
        description: 'Please upload an answer key CSV file first.'
      });
      return;
    }

    notificationController.info({
      message: 'Running Automatic Grading',
      description: 'Processing answer sheets... This may take a few moments.'
    });

    // TODO: Implement automatic grading logic
  };

  return (
    <>
      <PageTitle 
        title={`Grading - ${assessmentName}`}
        description="Grade assessment answer sheets" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        <PageWrapper>
          {/* Header */}
          <HeaderSection>
            <BackButton
              variant="ghost"
              businessSize="small"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              Back to Grading
            </BackButton>
            
            <div className="right-section">
              <span className="mode-label">Grading mode</span>
              <ToggleContainer>
                <ToggleButton 
                  mode={gradingMode}
                  onClick={() => setGradingMode(gradingMode === 'manual' ? 'automatic' : 'manual')}
                >
                  {gradingMode === 'manual' ? (
                    <>
                      <div className="icon-circle">
                        <span>✍️</span>
                      </div>
                      <span className="text">Manual</span>
                    </>
                  ) : (
                    <>
                      <span className="text">Automatic</span>
                      <div className="icon-circle">
                        <span>🤖</span>
                      </div>
                    </>
                  )}
                </ToggleButton>
              </ToggleContainer>
            </div>
          </HeaderSection>
          
          <TitleSection>
            <h1>
              <span className="icon">🧾</span>
              Grading – {assessmentName}
            </h1>
            
            <div className="subtitle">
              👥 {totalStudents} students • ❓ {totalQuestions} questions
            </div>
          </TitleSection>

          {/* Main Content */}
          <MainContent isAutoMode={gradingMode === 'automatic'}>
            {(() => {
              console.log('[GradingAssessment] 🎭 Current grading mode:', gradingMode);
              console.log('[GradingAssessment] 👀 Will show students panel:', gradingMode === 'manual');
              return null;
            })()}
            {/* Students Panel - Only show in manual mode */}
            {gradingMode === 'manual' && (
              <StudentsPanel
                title="Students"
                subtitle={loadingStudents ? 'Loading...' : `${students.filter((s: any) => s.status === 'approved').length} / ${totalStudents} approved`}
              >
              {loadingStudents ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16, color: '#666' }}>Loading students...</div>
                </div>
              ) : students.length > 0 ? (
                <StudentsList>
                  {(() => {
                    console.log('[GradingAssessment] 🎨 Rendering students list, count:', students.length);
                    console.log('[GradingAssessment] Students:', students.map(s => ({ id: s.id, name: s.name, answerSheetId: s.answerSheetId })));
                    return null;
                  })()}
                  {students.map((student: any) => {
                    const getStatusIcon = () => {
                      if (student.status === 'approved') return '✔';
                      if (student.status === 'pending') return '◔';
                      return null;
                    };
                    
                    const getStatusLabel = () => {
                      if (student.status === 'approved') return 'Approved';
                      if (student.status === 'pending') return 'Pending';
                      return null;
                    };
                    
                    return (
                      <StudentItem 
                        key={student.id}
                        selected={selectedStudent === student.id}
                        onClick={() => {
                          console.log('[GradingAssessment] 👆 StudentItem onClick triggered for:', student.id, student.name);
                          handleStudentClick(student.id);
                        }}
                      >
                        {getStatusIcon() && (
                          <div className={`status-icon ${student.status}`}>
                            {getStatusIcon()}
                          </div>
                        )}
                        
                        <div className="student-info">
                          <div className="name">{student.name}</div>
                          <div className="score">
                            {student.score !== null ? `${student.score}/${student.total}` : '—'}
                          </div>
                        </div>
                        
                        {getStatusLabel() && (
                          <div className={`status-label ${student.status}`}>
                            {getStatusLabel()}
                          </div>
                        )}
                      </StudentItem>
                    );
                  })}
                </StudentsList>
              ) : (
                <EmptyState>
                  <span className="icon">👥</span>
                  <div className="title">No Students</div>
                  <div className="description">No students found for this assessment</div>
                </EmptyState>
              )}
              
              {allStudentsApproved && students.length > 0 && (
                <div style={{ padding: '16px 20px', borderTop: '1px solid #e0e0e0' }}>
                  <BusinessButton
                    variant="success"
                    businessSize="large"
                    onClick={handleEndAssessment}
                    style={{ width: '100%' }}
                  >
                    ✅ Complete {(assessment?.type || 'Assessment').charAt(0).toUpperCase() + (assessment?.type || 'assessment').slice(1)}
                  </BusinessButton>
                </div>
              )}
              </StudentsPanel>
            )}

            {/* Center Panel - Changes based on mode */}
            {gradingMode === 'automatic' ? (
              <AutoModeContainer>
                {autoGradingStatus === 'completed' && autoGradingResults ? (
                  <div style={{ padding: '24px' }}>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
                        📊 Grading Results
                      </h3>
                      <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                        {autoGradingResults.gradedCount} / {autoGradingResults.totalStudents} students graded • 
                        {autoGradingResults.pendingApproval} pending approval
                      </p>
                    </div>
                    
                    <div style={{ maxHeight: '500px', overflowY: 'auto', marginBottom: 20 }}>
                      {autoGradingResults.students && autoGradingResults.students.map((student: any) => (
                        <div 
                          key={student.studentId}
                          style={{
                            padding: '12px 16px',
                            background: '#fff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', marginBottom: 4 }}>
                              {student.studentName}
                            </div>
                            <div style={{ fontSize: 12, color: '#999' }}>
                              {student.comments}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ 
                              fontSize: 18, 
                              fontWeight: 700, 
                              color: '#1677ff',
                              marginBottom: 4
                            }}>
                              {student.grade}
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              {student.score}/{student.totalScore}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <BusinessButton
                      variant="primary"
                      businessSize="large"
                      onClick={() => setShowApproveAllModal(true)}
                      style={{ width: '100%' }}
                    >
                      ✅ Approve All Students
                    </BusinessButton>
                  </div>
                ) : autoGradingStatus === 'processing' ? (
                  <AnswerSheetContainer alignTop={false}>
                    <div style={{ textAlign: 'center', color: '#666' }}>
                      <Spin size="large" />
                      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 20, marginBottom: 8 }}>
                        Processing...
                      </div>
                      <div style={{ fontSize: 14, color: '#999' }}>
                        Auto-grading in progress. This may take a few moments.
                      </div>
                    </div>
                  </AnswerSheetContainer>
                ) : (
                  <AnswerSheetContainer alignTop={true}>
                    <div style={{ textAlign: 'center', color: '#666', padding: '0' }}>
                      <div style={{ fontSize: 72, marginBottom: 20 }}>🤖</div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: '#52C41A', marginBottom: 12 }}>
                        Automatic Grading Mode
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.6, color: '#999', marginBottom: 24 }}>
                        Upload an answer key (CSV) on the right panel and click "Run Automatic Grading" to grade all students at once.
                      </div>
                      <div style={{ 
                        background: 'rgba(82, 196, 26, 0.05)', 
                        padding: '20px', 
                        borderRadius: '12px',
                        border: '1px solid rgba(82, 196, 26, 0.2)'
                      }}>
                        <div style={{ fontSize: 13, color: '#666', textAlign: 'left' }}>
                          <strong style={{ display: 'block', marginBottom: 12, color: '#52C41A' }}>
                            📋 How it works:
                          </strong>
                          <div style={{ marginBottom: 12 }}>
                            1. Upload a CSV file with correct answers<br/>
                            2. System will automatically compare student answers<br/>
                            3. Scores will be calculated and assigned<br/>
                            4. Review and approve results
                          </div>
                          <a 
                            href="/correct_answers.csv" 
                            download="correct_answers_template.csv"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: '#52C41A',
                              color: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '12px',
                              fontWeight: 600,
                              transition: 'all 0.3s',
                              marginTop: '4px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#389E0D'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#52C41A'}
                          >
                            ⬇️ Download CSV Template
                          </a>
                        </div>
                      </div>
                    </div>
                  </AnswerSheetContainer>
                )}
              </AutoModeContainer>
            ) : (
              <AnswerSheetViewer
                title="Answer Sheet"
                subtitle={selectedStudent ? students.find((s: any) => s.id === selectedStudent)?.name : 'Select a student to view their answer sheet'}
              >
                {selectedStudent ? (
                  <AnswerSheetContainer>
                    {loadingAnswerSheet ? (
                      <div style={{ textAlign: 'center', color: '#666' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
                        <div style={{ fontSize: 16 }}>Loading answer sheet...</div>
                      </div>
                    ) : answerSheet ? (
                      (() => {
                        // Determine image source: can be imageUrl, imageBase64, or url field
                        let imageSrc = answerSheet.imageUrl 
                          || answerSheet.url 
                          || (answerSheet.imageBase64 ? `data:image/png;base64,${answerSheet.imageBase64}` : null);
                        
                        console.log('[GradingAssessment] 🖼️ Raw image path from API:', imageSrc);
                        
                        // If imageSrc is a relative path (doesn't start with http/https/data), prepend BASE_URL
                        if (imageSrc && !imageSrc.startsWith('http') && !imageSrc.startsWith('data:')) {
                          imageSrc = `${URLs.BASE_URL}${imageSrc.startsWith('/') ? imageSrc.slice(1) : imageSrc}`;
                          console.log('[GradingAssessment] 🔗 Converted to absolute URL:', imageSrc);
                        }
                        
                        if (imageSrc) {
                          return (
                            <img 
                              src={imageSrc} 
                              alt={`Answer sheet for ${students.find((s: any) => s.id === selectedStudent)?.name}`}
                              style={{ maxWidth: '100%', height: 'auto' }}
                              onError={(e) => {
                                console.error('[GradingAssessment] ❌ Image failed to load:', imageSrc);
                                e.currentTarget.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('[GradingAssessment] ✅ Image loaded successfully:', imageSrc);
                              }}
                            />
                          );
                        } else {
                          return (
                            <div style={{ textAlign: 'center', color: '#999' }}>
                              <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
                              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                                Answer Sheet Data Invalid
                              </div>
                              <div style={{ fontSize: 14 }}>
                                No valid image URL or Base64 data found
                              </div>
                            </div>
                          );
                        }
                      })()
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <div style={{ fontSize: 64, marginBottom: 16 }}>📄</div>
                        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                          No Answer Sheet
                        </div>
                        <div style={{ fontSize: 14 }}>
                          {students.find((s: any) => s.id === selectedStudent)?.name} hasn't uploaded an answer sheet yet
                        </div>
                      </div>
                    )}
                  </AnswerSheetContainer>
                ) : (
                  <EmptyState>
                    <span className="icon">📋</span>
                    <div className="title">No Student Selected</div>
                    <div className="description">Select a student from the list to view their answer sheet</div>
                  </EmptyState>
                )}
              </AnswerSheetViewer>
            )}

            {/* Right Panel - Changes based on mode */}
            {gradingMode === 'automatic' ? (
              <ManualGradingPanel title="Automatic Grading">
                <AutomaticModeSection>
                  <UploadSection>
                    <label className="upload-label">Upload Answer Key (.csv)</label>
                    <StyledUpload
                      accept=".csv"
                      beforeUpload={(file) => {
                        setAnswerKeyFile(file);
                        return false;
                      }}
                      fileList={answerKeyFile ? [answerKeyFile] : []}
                      onRemove={() => setAnswerKeyFile(null)}
                      disabled={autoGradingStatus === 'processing'}
                    >
                      <BusinessButton 
                        variant="secondary" 
                        businessSize="medium"
                        style={{ width: '100%' }}
                        disabled={autoGradingStatus === 'processing'}
                      >
                        <UploadOutlined /> Select CSV File
                      </BusinessButton>
                    </StyledUpload>
                    <div className="upload-hint">
                      Upload a CSV file with correct answers for automatic grading
                    </div>
                  </UploadSection>

                  <ActionButton
                    variant="primary"
                    businessSize="large"
                    disabled={!answerKeyFile || autoGradingStatus === 'processing'}
                    onClick={handleRunAutomaticGrading}
                    loading={autoGradingStatus === 'processing'}
                  >
                    {autoGradingStatus === 'processing' ? (
                      <>⏳ Processing...</>
                    ) : (
                      <><PlayCircleOutlined /> Run Automatic Grading</>
                    )}
                  </ActionButton>
                </AutomaticModeSection>
              </ManualGradingPanel>
            ) : selectedStudent ? (
              <ManualGradingPanel
                title="Manual Grading"
                subtitle="Enter score and approve"
              >
                <GradingScaleSection>
                  <label className="scale-title">
                    Grading Scale 
                    {gradingScaleData?.type && (
                      <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400, marginLeft: 8 }}>
                        ({gradingScaleData.type})
                      </span>
                    )}
                  </label>
                  {gradingScale.map((scale) => {
                    const scaleType = gradingScaleData?.type?.toLowerCase();
                    const suffix = scaleType === 'percentage' ? '%' : '';
                    
                    return (
                      <GradeItem key={scale.id || scale.grade}>
                        <span className="grade-letter">{scale.grade}:</span>
                        <span className="grade-range">
                          {scale.min}{suffix}–{scale.max}{suffix}
                        </span>
                      </GradeItem>
                    );
                  })}
                  {!gradingScaleData && assessment && (
                    <div style={{ fontSize: 13, color: '#ff4d4f', marginTop: 8, padding: '8px 12px', background: 'rgba(255, 77, 79, 0.08)', borderRadius: 6 }}>
                      ⚠️ No grading scale assigned to this assessment
                      {assessment.gradingScaleId && (
                        <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                          Grading Scale ID: {assessment.gradingScaleId} (not found)
                        </div>
                      )}
                    </div>
                  )}
                </GradingScaleSection>

                <ScoreInputSection>
                  <label className="input-label">Correct Answers:</label>
                  <ScoreInput
                    type="text"
                    placeholder={`0 / ${totalQuestions}`}
                    value={correctAnswers}
                    onChange={handleCorrectAnswersChange}
                    max={totalQuestions}
                  />
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                    Enter the number of correct answers (0-{totalQuestions})
                  </div>
                </ScoreInputSection>

                {finalScore && gradingScaleData && (
                  <GradeResultSection>
                    <div className="grade-label">Grade</div>
                    <div className="grade-value">{calculateGrade(parseInt(finalScore))}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
                      {totalQuestions > 0 && (
                        <>
                          {correctAnswers} / {totalQuestions} correct = {Math.round((parseInt(correctAnswers || '0') / totalQuestions) * 100)}%
                        </>
                      )}
                    </div>
                  </GradeResultSection>
                )}

                <ApproveButton
                  variant="primary"
                  businessSize="large"
                  onClick={handleApproveStudent}
                  disabled={!correctAnswers || !finalScore}
                >
                  Approve Student
                </ApproveButton>
              </ManualGradingPanel>
            ) : (
              <ManualGradingPanel
                title="Manual Grading"
                subtitle="Select a student to start"
              >
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>👈</div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                    No Student Selected
                  </div>
                  <div style={{ fontSize: 14 }}>
                    Select a student from the list to start manual grading
                  </div>
                </div>
              </ManualGradingPanel>
            )}
          </MainContent>
        </PageWrapper>
      </PageContainer>
      
      {/* Approve All Confirmation Modal */}
      <Modal
        title="Approve All Students"
        open={showApproveAllModal}
        onOk={handleApproveAll}
        onCancel={() => setShowApproveAllModal(false)}
        okText="Yes, Approve All"
        cancelText="Cancel"
        okButtonProps={{ danger: false }}
      >
        <p>Are you sure you want to approve all {autoGradingResults?.gradedCount || 0} students?</p>
        <p style={{ fontSize: 13, color: '#666', marginTop: 12 }}>
          This will finalize their grades and they will be marked as approved.
        </p>
      </Modal>
    </>
  );
};

export default GradingAssessmentPage;

